import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  setCategoryFilter,
  setCheckboxFilter,
  setCurrentPageFilter,
  setFilterAllData,
  setLimitFilter,
  setOffsetFilter,
  setRequestFilter,
  setSortFilter,
  setTypeFilter,
} from '../../store/filterSlice';
import { CatalogSection } from '../../components/CatalogSection';
import { buildUrlParams } from '../../utils/filterUtils';
import { useGetFilterProductsQuery } from '../../api/productsApi';
import { findTitleByCategory, loadFromLocalStorage } from '../../utils/utils';
import { filterOptions } from '../../utils/filterData';
import { messages } from '../../utils/data';
import Seo from '../../seo/Seo';
import { CATALOG_SECTIONS, STATIC_META } from '../../seo/metaConfig';

import { FilterList } from './components/FilterList/FilterList';
import { ShowList } from './components/ShowList/ShowList';
import { SearchForm } from './components/SearchForm';
import { Sort } from './components/Sort';
import './Catalog.scss';

export const Catalog = () => {
  const filter = useSelector(state => state.filter);
  const [paramsUrl, setParamsUrl] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);

  // Раздел каталога из адреса: /catalog/doors, /catalog/entrance-doors, /catalog/windows
  const { section } = useParams();
  const sectionKey = String(section || '').toLowerCase();
  const catalogSection = Object.prototype.hasOwnProperty.call(
    CATALOG_SECTIONS,
    sectionKey,
  )
    ? CATALOG_SECTIONS[sectionKey]
    : undefined;
  const seoMeta = catalogSection ? catalogSection.meta : STATIC_META.catalog;

  // Для сокрытия строки поиска при скролле
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);

  // Состояние для отслеживания ширины экрана
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const dispatch = useDispatch();

  // Раздел «Каталог дверей» показывает сразу две группы товаров - межкомнатные
  // и входные двери. Бэкенд отдаёт товары только одного типа за запрос, поэтому
  // здесь берём обе группы и собираем список на странице.
  const groupTypes = catalogSection && catalogSection.types;
  // Как только посетитель сам выбрал фильтр или что-то ищет,
  // страница работает как обычный каталог.
  const isGroup =
    Boolean(groupTypes) && !filter.search && !filter.type && !filter.category;

  const {
    data: ProductsData = [],
    error,
    isLoading,
  } = useGetFilterProductsQuery([paramsUrl], { skip: isGroup });

  const groupParams = useMemo(() => {
    if (!groupTypes) return ['', ''];
    return groupTypes.map(type =>
      buildUrlParams({
        ...filter,
        type,
        category: '',
        limit: 1000,
        offset: 0,
      }),
    );
  }, [groupTypes, filter]);

  const firstGroup = useGetFilterProductsQuery([groupParams[0]], {
    skip: !isGroup,
  });
  const secondGroup = useGetFilterProductsQuery([groupParams[1]], {
    skip: !isGroup,
  });

  const groupItems = useMemo(() => {
    if (!isGroup) return [];
    const items = [
      ...((firstGroup.data && firstGroup.data.results) || []),
      ...((secondGroup.data && secondGroup.data.results) || []),
    ];
    return items.sort((a, b) =>
      filter.ordering === 'descending' ? b.price - a.price : a.price - b.price,
    );
  }, [isGroup, firstGroup.data, secondGroup.data, filter.ordering]);

  const listLoading = isGroup
    ? firstGroup.isLoading || secondGroup.isLoading
    : isLoading;
  const listError = isGroup ? firstGroup.error || secondGroup.error : error;

  const infoMessage = listLoading
    ? messages.loadMessage
    : listError
      ? messages.errorMessage
      : '';

  const listData = isGroup
    ? groupItems.slice(filter.offset, filter.offset + filter.limit)
    : ProductsData.results || [];

  const totalPages = Math.ceil(
    (isGroup ? groupItems.length : ProductsData.count) / filter.limit,
  );

  // Обработчик изменения строки поиска
  const searchHandler = value => {
    dispatch(setRequestFilter(value));
    handleResetPagination();
  };

  // Проверяем LS, если там есть состояния фильтров, то отправляем их в стор
  useEffect(() => {
    const storedFilter = loadFromLocalStorage('filter');
    if (storedFilter) {
      dispatch(setFilterAllData(storedFilter));
    }
  }, [dispatch]);

  // Если открыт раздел каталога со своим адресом - ставим его фильтр.
  // Идёт после чтения localStorage, чтобы адрес страницы был главнее сохранённого фильтра.
  useEffect(() => {
    if (!catalogSection) return;
    // У раздела из двух групп своего одиночного типа нет - фильтр очищаем,
    // список собирается из двух запросов ниже.
    dispatch(setTypeFilter(catalogSection.type || ''));
    dispatch(setCategoryFilter(''));
    // Сохранённый поиск тоже сбрасываем: иначе на адресе раздела
    // под его заголовком показался бы прошлый результат поиска.
    dispatch(setRequestFilter(''));
    dispatch(setCurrentPageFilter(1));
    dispatch(setOffsetFilter(0));
  }, [catalogSection, dispatch]);

  // Определение limit
  useEffect(() => {
    if (windowWidth >= 1457) {
      dispatch(setLimitFilter(8));
    } else if (windowWidth < 1440 && windowWidth >= 1124) {
      dispatch(setLimitFilter(9));
    } else if (windowWidth < 1124 && windowWidth >= 1025) {
      dispatch(setLimitFilter(6));
    } else if (windowWidth < 1025 && windowWidth >= 1015) {
      dispatch(setLimitFilter(8));
    } else if (windowWidth < 1015 && windowWidth >= 766) {
      dispatch(setLimitFilter(9));
    } else if (windowWidth < 766 && windowWidth >= 751) {
      dispatch(setLimitFilter(6));
    } else if (windowWidth < 751 && windowWidth >= 630) {
      dispatch(setLimitFilter(8));
    } else if (windowWidth < 630 && windowWidth >= 468) {
      dispatch(setLimitFilter(9));
    } else {
      dispatch(setLimitFilter(6));
    }
  }, [windowWidth]);

  // Обработка изменений фильтра сортировки
  useEffect(() => {
    updateParamsUrl(filter);
  }, [filter.ordering, filter]);

  // useEffect для отслеживания изменений размеров окна
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Для сокрытия строки поиска при скролле
  useEffect(() => {
    const handleScroll = () => {
      const currentPosition = window.scrollY;
      setPreviousScrollPosition(currentPosition);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isSearchBarHidden = useMemo(
    () => window.innerWidth < 490 && previousScrollPosition > 0,
    [previousScrollPosition],
  );

  const sortHandler = btnId => {
    dispatch(setSortFilter(btnId));
    handleResetPagination();
  };

  // Обработчик изменения флажков (checkbox)
  const checkboxHandler = key => {
    dispatch(setCheckboxFilter(key));
    handleResetPagination();
  };

  // Обработчик изменения флажков (select)
  const selectHandler = key => {
    dispatch(setCategoryFilter(''));
    dispatch(setTypeFilter(key));
    handleResetPagination();
  };

  // Обработчик изменения флажков (radio)
  const radioHandler = key => {
    dispatch(setTypeFilter('interior_door'));
    dispatch(setCategoryFilter(key));
    handleResetPagination();
  };

  // Функция для обработки отфильтрованных данных перед запросом на сервер
  function updateParamsUrl(filters) {
    const paramsUrl = buildUrlParams(filters);
    setParamsUrl(paramsUrl);
  }

  // Обработчик отправки формы с фильтрами
  const handleSubmit = evt => {
    evt.preventDefault();
    updateParamsUrl(filter);
  };

  function handleMenuClick() {
    setMenuOpen(!menuOpen);
  }

  useEffect(() => {
    if (menuOpen) {
      document.body.classList.add('page_lock');
    } else {
      document.body.classList.remove('page_lock');
    }
  }, [menuOpen]);

  const handleResetFilter = () => {
    dispatch(setTypeFilter(''));
    dispatch(setCategoryFilter(''));
    handleResetPagination();
  };

  const showCatalogList =
    filter.search || filter.type || filter.category || isGroup;

  const handlePageChange = page => {
    dispatch(setCurrentPageFilter(page));
    dispatch(setOffsetFilter(filter.limit * (page - 1)));
  };

  const handleResetPagination = () => {
    dispatch(setCurrentPageFilter(1));
    dispatch(setOffsetFilter(0));
  };

  return (
    <>
      <Seo {...seoMeta} />
      <section className="catalog">
        <div
          className={`catalog__background ${
            (menuOpen && ' catalog__background_active') || ''
          }`}
          onClick={handleMenuClick}
        />

        <div className="wrapper">
          <div className="catalog__filter-wrapper">
            {showCatalogList && (
              <button
                className="catalog__filter-button catalog__filter-button_back"
                type="button"
                onClick={handleResetFilter}
                aria-label="Вернуться в начало каталога"
              ></button>
            )}
            <h1 className="catalog__title" onClick={handleResetFilter}>
              Каталог
            </h1>
            <h1 className="catalog__title_s">
              {isGroup
                ? 'Двери'
                : showCatalogList
                  ? findTitleByCategory(filter['type'], filterOptions)
                  : 'Каталог'}
            </h1>
          </div>
          <p className="catalog__subtitle_s">
            {findTitleByCategory(filter['category'], filterOptions)}
          </p>
          <div
            className={
              isSearchBarHidden
                ? 'catalog__container catalog__container_small'
                : 'catalog__container'
            }
          >
            <div
              className={`catalog__filter-menu${
                (menuOpen && ' catalog__filter-menu_active') || ''
              }`}
            >
              <div className="catalog__filter-wrapper">
                <button
                  className="catalog__filter-button"
                  type="button"
                  onClick={handleMenuClick}
                  aria-label="Выйти из меню фильтров"
                ></button>
                <p className="catalog__filter-title">Фильтр</p>
              </div>
              <FilterList
                filter={filter}
                checkboxHandler={checkboxHandler}
                selectHandler={selectHandler}
                radioHandler={radioHandler}
                handleSubmit={handleSubmit}
                onMenuClick={handleMenuClick}
                windowWidth={windowWidth}
              />
            </div>
            {showCatalogList && (
              <button
                className="catalog__button-reset"
                type="button"
                onClick={handleResetFilter}
              >
                Каталог
              </button>
            )}
            <Sort sortHandler={sortHandler} />
            <SearchForm
              searchHandler={searchHandler}
              isSearchBarHidden={isSearchBarHidden}
            />
            <button
              className="catalog__button"
              type="button"
              onClick={handleMenuClick}
              aria-label="Войти в меню фильтров"
            />
            {windowWidth > 750 &&
              (showCatalogList ? (
                <ShowList
                  data={listData}
                  message={infoMessage}
                  currentPage={filter.currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              ) : (
                <CatalogSection type="catalog" />
              ))}
          </div>
        </div>
      </section>
      {windowWidth <= 750 &&
        (showCatalogList ? (
          <ShowList
            data={listData}
            message={infoMessage}
            currentPage={filter.currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        ) : (
          <CatalogSection type="catalog" />
        ))}
    </>
  );
};
